import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { apiPost } from '../../../utils/apiClient';
import { readCookie, writeGettime, writeLastStage } from '../../../utils/santaCookies';
import { startSantaStage, submitSantaStage } from '../../../utils/roundSave';
import { IMG, STAGE_NEXT, pageBg } from '../../../data/santaStages';
import { Puzzle } from '../../../utils/jigsaw';
import GameBanner from '../../../components/GameBanner/GameBanner';
import './StagePage.css';

const OK = { backgroundColor: '#75e375', color: 'black' };
const BAD = { backgroundColor: 'red', color: 'white' };
const CLEAR = { backgroundColor: 'white', color: 'black' };

function formatClock(minutes, seconds) {
  const mm = minutes ? (minutes > 9 ? minutes : `0${minutes}`) : '00';
  const ss = seconds > 9 ? seconds : `0${seconds}`;
  return `${mm}:${ss}`;
}

export default function StagePage({ stage }) {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [points, setPoints] = useState(0);
  const [clock, setClock] = useState('');
  const [saving, setSaving] = useState(false);
  const [solved, setSolved] = useState(false);
  const [fieldStyle, setFieldStyle] = useState({});
  const [locked, setLocked] = useState({});
  const timerRef = useRef({ hours: 0, minutes: 0, seconds: 0 });
  const clockRef = useRef('');
  const usedPairs = useRef(new Set());
  const puzzleRef = useRef(null);
  const scoredName = useRef(false);
  const scoredCode = useRef(false);

  useEffect(() => {
    if (!user?.userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (stage === 1) await startSantaStage(user);
        const res = await apiPost('/game/status', {
          userId: user.userId,
          stage,
          gettime: readCookie('gettime'),
          lastStage: readCookie('last_stage'),
        });
        if (cancelled) return;
        if (res.timer) {
          writeGettime(res.timer.gettime);
          writeLastStage(res.timer.lastStage);
          timerRef.current = {
            hours: Number(res.timer.hours) || 0,
            minutes: Number(res.timer.minutes) || 0,
            seconds: Number(res.timer.seconds) || 0,
          };
        }
        setPoints(stage === 1 ? 0 : Number(res.totalScore) || 0);
        if (res.redirectTo) {
          navigate(res.redirectTo, { replace: true });
          return;
        }
        setReady(true);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('Unable to load the game. Please refresh.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, stage, navigate]);

  useEffect(() => {
    if (!ready) return undefined;
    const id = setInterval(() => {
      let { seconds, minutes, hours } = timerRef.current;
      seconds += 1;
      if (seconds >= 60) {
        seconds = 0;
        minutes += 1;
        if (minutes >= 60) {
          minutes = 0;
          hours += 1;
        }
      }
      timerRef.current = { seconds, minutes, hours };
      const target = formatClock(minutes, seconds);
      clockRef.current = target;
      setClock(target);
    }, 1000);
    return () => clearInterval(id);
  }, [ready]);

  useEffect(() => {
    if (!ready || stage !== 4 || !puzzleRef.current) return undefined;
    const el = puzzleRef.current;
    const puzzle = new Puzzle({
      el,
      image: `${IMG}/jigsaw2.png`,
      difficulty: 'expert',
      dragenter(evt) {
        if (!evt.target.draggable) evt.target.classList.add('highlight');
      },
      dragleave(evt) {
        if (!evt.target.draggable) evt.target.classList.remove('highlight');
      },
      mousedown(evt) {
        if (evt.target.children && evt.target.children[0]) evt.target.children[0].classList.add('highlight');
      },
      mouseup(evt) {
        if (evt.target.children && evt.target.children[0]) evt.target.children[0].classList.remove('highlight');
      },
      finished() {
        window.setTimeout(() => setSolved(true), 800);
      },
    });
    puzzle.init();
    return () => {
      el.innerHTML = '';
    };
  }, [ready, stage]);

  const markField = (id, style, lock) => {
    setFieldStyle((prev) => ({ ...prev, [id]: style }));
    if (lock) setLocked((prev) => ({ ...prev, [id]: true }));
  };

  const onLyricChange = (answer) => (event) => {
    const input = event.target;
    if (locked[input.id]) return;
    const trimmed = input.value.trim();
    if (trimmed === '') {
      input.value = '';
      markField(input.id, CLEAR, false);
      return;
    }
    if (trimmed.toLowerCase() === answer) {
      markField(input.id, OK, true);
      setPoints((prev) => prev + 1);
    } else {
      markField(input.id, BAD, false);
    }
  };

  const scoreFootprintRow = (leftId, rightId) => {
    const left = document.getElementById(leftId);
    const right = document.getElementById(rightId);
    if (!left || !right || locked[leftId] || locked[rightId]) return;
    const leftVal = left.value.toLowerCase();
    const rightVal = right.value.toLowerCase();
    const key = `${leftVal}-${rightVal}`;
    const match = (leftVal === 'a' && rightVal === 'elfie') || (leftVal === 'd' && rightVal === 'burgermeiste');
    if (match && !usedPairs.current.has(key)) {
      markField(leftId, OK, true);
      markField(rightId, OK, true);
      usedPairs.current.add(key);
      setPoints((prev) => prev + 1);
      return;
    }
    markField(leftId, BAD, false);
    markField(rightId, BAD, false);
  };

  const onFootprintChange = (event) => {
    const id = event.target.id;
    if (id === 'ans1' || id === 'ans3') scoreFootprintRow('ans1', 'ans3');
    if (id === 'ans2' || id === 'ans4') scoreFootprintRow('ans2', 'ans4');
  };

  const onCodeKeyUp = (event) => {
    const input = event.target;
    const value = input.value.trim();
    if (locked.ans4) return;
    if (value !== '' && value === '2978') {
      markField(input.id, OK, true);
    } else if (value === '') {
      markField(input.id, CLEAR, false);
    } else {
      markField(input.id, BAD, false);
    }
  };

  const onCodeChange = (event) => {
    const input = event.target;
    const value = input.value.trim();
    if (value !== '' && value === '2978') {
      if (!scoredCode.current) {
        scoredCode.current = true;
        setPoints((prev) => prev + 1);
      }
    } else if (value === '') {
      input.value = '';
    }
  };

  const onNameChange = (event) => {
    const input = event.target;
    const trimmed = input.value.trim();
    if (locked.ans1) return;
    if (trimmed === '') {
      input.value = '';
      markField(input.id, CLEAR, false);
      return;
    }
    const value = input.value.toLowerCase();
    if (value === 'santa claus' || value === 'santa' || value === 'santaclaus') {
      markField(input.id, OK, true);
      if (!scoredName.current) {
        scoredName.current = true;
        setPoints((prev) => prev + 1);
      }
    } else {
      markField(input.id, BAD, false);
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const form = event.currentTarget;
    if (stage === 4 && !solved) return;
    if (!form.reportValidity()) return;
    const answers = {};
    Array.from(form.elements).forEach((el) => {
      if (el.name && el.name.startsWith('ans')) answers[el.name] = el.value;
    });
    setSaving(true);
    try {
      await submitSantaStage({
        user,
        question: stage,
        timer: clockRef.current,
        answers,
        navigate,
        nextPath: STAGE_NEXT[stage],
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        text: 'Unable to save your answer. Please try again.',
        icon: 'error',
        confirmButtonText: 'Close',
        customClass: { confirmButton: 'hf-swal-close', popup: 'hf-swal-popup' },
        buttonsStyling: false,
      });
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') event.preventDefault();
  };

  const bg = pageBg(stage === 4 ? 'gamebg1.jpg' : 'gamebg.jpg', 'bg1mob.jpg');

  if (error) return <div className="santa-stage santa-message" style={bg}>{error}</div>;
  if (!ready) return <div className="santa-stage santa-message" style={bg}>Loading…</div>;

  return (
    <div className={`santa-stage santa-stage-${stage}${solved ? ' is-solved' : ''}`} style={bg}>
      <GameBanner />
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <div className="timer-back" id="timer1">{clock ? `Time: ${clock}` : 'Time : 00'}</div>
          </div>
          <div className="col-md-2" style={{ float: 'right' }}>
            <div className="score">Points : <span id="score1">{points}</span></div>
          </div>
        </div>
      </div>
      <form id="formdata" onSubmit={onSubmit} onKeyDown={onKeyDown}>
        <input type="hidden" name="timer" id="time" value={clock} readOnly />
        {stage === 1 ? <StageOne onLyricChange={onLyricChange} saving={saving} fieldStyle={fieldStyle} locked={locked} /> : null}
        {stage === 2 ? <StageTwo onFootprintChange={onFootprintChange} saving={saving} fieldStyle={fieldStyle} locked={locked} /> : null}
        {stage === 3 ? <StageThree onCodeKeyUp={onCodeKeyUp} onCodeChange={onCodeChange} saving={saving} fieldStyle={fieldStyle} locked={locked} /> : null}
        {stage === 4 ? <StageFour puzzleRef={puzzleRef} onNameChange={onNameChange} saving={saving} fieldStyle={fieldStyle} locked={locked} /> : null}
      </form>
    </div>
  );
}

function StageOne({ onLyricChange, saving, fieldStyle, locked }) {
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-10 col-md-10 col-lg-10 col-xs-12 auto">
          <h3 className="log-title" style={{ textAlign: 'center' }}>
            <b>
              It’s a Christmas calamity! Santa Claus has been kidnapped two days before the big delivery. Where could he be? The bells are ringing,the elves are singing,For the spirit of Christmas to be revived Or could they be telling a story with their lyrics That needs to be better identified?
            </b>
          </h3>
        </div>
      </div>
      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-sm-6 col-md-6 col-lg-6 col-xs-12 auto">
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1" style={{ marginBottom: 10 }}>
            <div className="col-sm-12 auto" style={{ color: 'white' }}>
              1. Joy to the
              <input type="text" name="ans1" id="ans1" className="input-text" autoComplete="off" required readOnly={!!locked.ans1} style={fieldStyle.ans1} onChange={onLyricChange('world')} />
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1" style={{ marginBottom: 10 }}>
            <div className="col-sm-12 auto" style={{ color: 'white' }}>
              2. Jingle Bells Jingle Bells, Jingle
              <input type="text" name="ans2" id="ans2" className="input-text" autoComplete="off" required readOnly={!!locked.ans2} style={fieldStyle.ans2} onChange={onLyricChange('all')} />
              {' '}the Way
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1" style={{ marginBottom: 10 }}>
            <div className="col-sm-12 auto" style={{ color: 'white' }}>
              3. Rudolph the
              <input type="text" name="ans3" id="ans3" className="input-text" autoComplete="off" required readOnly={!!locked.ans3} style={fieldStyle.ans3} onChange={onLyricChange('red')} />
              {' '}nose Reindeer
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1" style={{ marginBottom: 10 }}>
            <div className="col-sm-12 auto" style={{ color: 'white' }}>
              4. We wish you a
              <input type="text" name="ans4" id="ans4" className="input-text" autoComplete="off" required readOnly={!!locked.ans4} style={fieldStyle.ans4} onChange={onLyricChange('merry')} />
              {' '}Christmas
            </div>
          </div>
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1" style={{ marginTop: 20 }}>
            <div className="col-sm-12 auto" style={{ fontWeight: 'bold', color: 'white' }}>
              Enter the 4 letter Code
              <input type="text" name="ans5" id="ans5" className="input-text" autoComplete="off" required readOnly={!!locked.ans5} style={fieldStyle.ans5} onChange={onLyricChange('warm')} />
            </div>
          </div>
        </div>
      </div>
      <div className="row show-btn">
        <div className="col-sm-2 col-md-2 col-lg-1 col-xs-6 auto">
          <input type="submit" value="Submit" className="button-login" style={{ marginBottom: 10, width: '100%' }} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

function FootprintSelect({ name, options, onChange, fieldStyle, locked }) {
  return (
    <select name={name} id={name} className="input-text" required defaultValue="" disabled={!!locked[name]} style={fieldStyle[name]} onChange={onChange}>
      <option value="" disabled style={{ color: 'black' }}>Select Option</option>
      {options.map(([value, label]) => (
        <option key={value} value={value}>{label}</option>
      ))}
    </select>
  );
}

const LETTERS = [['a', 'A'], ['b', 'B'], ['c', 'C'], ['d', 'D']];
const SUSPECTS = [
  ['grinch', 'Grinch'],
  ['jessica_claus', 'Jessica Claus'],
  ['cookie_monster', 'Cookie Monster'],
  ['burgermeiste', 'Meisterburger Burgermeister'],
  ['elfie', 'Elfie'],
];

function StageTwo({ onFootprintChange, saving, fieldStyle, locked }) {
  return (
    <div className="container-fluid">
      <div className="col-sm-9 col-md-9 col-lg-9 col-xs-12 auto">
        <div className="row">
          <h3 className="log-title" style={{ textAlign: 'center', marginTop: -11 }}>
            <b>
              Thank you all for deciphering the voice of the elves. Your group did an excellent job! You have unveiled the last know location of Santa – The WARM WINTER store! The team has identified footprints outside this store. Can you match the footprints to those of the suspects?
            </b>
          </h3>
        </div>
        <div className="row">
          <div className="col-sm-6 col-md-6 col-lg-6 col-xs-12">
            <div className="col-sm-11 col-md-11 col-lg-11 col-xs-12 auto">
              <img src={`${IMG}/leftfoot.png`} style={{ width: '100%' }} alt="" />
            </div>
          </div>
          <div className="col-sm-6 col-md-6 col-lg-6 col-xs-12">
            <img src={`${IMG}/rightfoot.png`} style={{ width: '100%' }} alt="" />
          </div>
        </div>
        <div className="row">
          <div className="col-sm-11 col-md-11 col-lg-11 col-xs-12 auto">
            <div className="col-md-10 col-lg-10 col-sm-12 col-xs-12 auto">
              <div className="row foot-labels" style={{ marginTop: 10 }}>
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <div className="game-title1">Shoe Print (A/B/C/D)</div>
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <div className="game-title1">Footprints of the suspects</div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <FootprintSelect name="ans1" options={LETTERS} onChange={onFootprintChange} fieldStyle={fieldStyle} locked={locked} />
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <FootprintSelect name="ans3" options={SUSPECTS} onChange={onFootprintChange} fieldStyle={fieldStyle} locked={locked} />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <FootprintSelect name="ans2" options={LETTERS} onChange={onFootprintChange} fieldStyle={fieldStyle} locked={locked} />
                </div>
                <div className="col-xs-12 col-sm-6 col-md-6 col-lg-6">
                  <FootprintSelect name="ans4" options={SUSPECTS} onChange={onFootprintChange} fieldStyle={fieldStyle} locked={locked} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row show-btn">
        <div className="col-sm-2 col-md-2 col-lg-2 col-xs-6 auto">
          <input type="submit" value="Lock in Suspect" className="button-login" style={{ marginBottom: 10, width: '100%' }} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

function StageThree({ onCodeKeyUp, onCodeChange, saving, fieldStyle, locked }) {
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-10 col-md-10 col-lg-10 col-xs-12 auto">
          <h3 className="log-title" style={{ textAlign: 'center' }}>
            <b>
              The Workshop has figured out that Santa Claus was kidnapped immediately after feeding the reindeers. It took place right outside of The WARM WINTER store at 3 PM. Unlock the code that can help you enter the doors of this store.
            </b>
          </h3>
        </div>
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <div className="col-sm-5 col-md-5 col-lg-5 col-xs-12 auto">
          <ClueRow image="ques1.png" name="ans1" value="3621" />
          <ClueRow image="ques2.png" name="ans2" value="2510" />
          <ClueRow image="ques3.png" name="ans3" value="835" />
          <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1">
            <label className="control-label col-sm-7 col-md-7 col-lg-7 col-xs-8">
              <img src={`${IMG}/ques4.png`} style={{ width: '100%' }} alt="" />
            </label>
            <div className="col-sm-5 col-md-5 col-lg-5 col-xs-4">
              <input
                type="text"
                name="ans4"
                id="ans4"
                className="input-text code-input"
                autoComplete="off"
                required
                readOnly={!!locked.ans4}
                style={fieldStyle.ans4}
                onKeyUp={onCodeKeyUp}
                onChange={onCodeChange}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="row show-btn">
        <div className="col-sm-2 col-md-2 col-lg-2 col-xs-6 auto">
          <input type="submit" value="Submit" className="button-login" style={{ marginBottom: 10, width: '100%' }} disabled={saving} />
        </div>
      </div>
    </div>
  );
}

function ClueRow({ image, name, value }) {
  return (
    <div className="col-sm-12 col-md-12 col-lg-12 col-xs-12 form-group game-title1">
      <label className="control-label col-sm-7 col-md-7 col-lg-7 col-xs-8">
        <img src={`${IMG}/${image}`} style={{ width: '100%' }} alt="" />
      </label>
      <div className="col-sm-5 col-md-5 col-lg-5 col-xs-4">
        <input type="text" name={name} id={name} value={value} className="input-text santa-clue" autoComplete="off" readOnly required />
      </div>
    </div>
  );
}

function StageFour({ puzzleRef, onNameChange, saving, fieldStyle, locked }) {
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-sm-8 col-md-8 col-lg-8 col-xs-12 auto">
          <h3 className="log-title" style={{ textAlign: 'center' }}>
            <b>
              You have cracked open the door but you don&apos;t see or hear any person. Except from the corner of your eye, you spot a shadow near the storage room. Solve the jigsaw and unveil the real Kidnapper!
            </b>
          </h3>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-8 col-md-8 col-lg-8 col-xs-12 auto">
          <div className="col-sm-5 col-md-5 col-lg-5 col-xs-12">
            <div id="puzzle1" ref={puzzleRef} style={{ padding: 5 }} />
            <img src={`${IMG}/jigsaw2.png`} className="finalimg" id="finalimg" style={{ padding: 15 }} alt="" />
          </div>
          <div className="col-sm-7 col-md-7 col-lg-7 col-xs-12 form-group game-title1">
            <div className="row">
              <div className="col-sm-12 auto showtext" style={{ color: 'white', textAlign: 'center' }}>
                Insert the Kidnapper’s name:
                <input type="text" name="ans1" id="ans1" className="input-text" autoComplete="off" required readOnly={!!locked.ans1} style={fieldStyle.ans1} onChange={onNameChange} />
              </div>
            </div>
            <div className="row show-btn">
              <div className="col-sm-4 col-md-4 col-lg-4 col-xs-6 auto">
                <input type="submit" value="Submit" className="button-login santa-stage4-submit" style={{ marginBottom: 10, width: '100%' }} disabled={saving} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

